import * as stylex from '@stylexjs/stylex';
import clsx from 'clsx';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '@/core/Checkbox.atom';
import { IconSelect } from '@/core/IconSelect.molecule';
import { ServerSideFilteringAndPaginatingTable } from '@/core/ServerSideFilteringAndPaginatingTable.molecule';
import { SortIcon } from '@/core/SortIcon.atom';
import { CELL_TYPES } from '@/core/Table.atom';
import { sqWorkbookStore } from '@/core/core.stores';
import { useFluxPath } from '@/core/hooks/useFluxPath.hook';
import { SearchResults } from '@/core/searchBar/SearchResults.molecule';
import {
  useExploreWorkbookModalFolder,
  useExploreWorkbookModalInitialFolderId,
  useExploreWorkbookModalItemTotalForTable,
  useExploreWorkbookModalItems,
  useExploreWorkbookModalLoading,
  useExploreWorkbookModalPageNumber,
  useExploreWorkbookModalPageSize,
  useExploreWorkbookModalSearchParams,
  useExploreWorkbookModalShowResults,
  useExploreWorkbookModalSortAscending,
  useExploreWorkbookModalSortProperty,
  useExploreWorkbookModalTableFilter,
} from '@/explorer/exploreWorkbookModal.store';
import { HomeScreenItemIconCell, NameCell } from '@/homescreen/CellRender.atom';
import { HOME_SCREEN_TABS, SEARCH_ITEM_LOCATIONS, SEARCH_LOCATION_OPTIONS } from '@/main/app.constants';
import { getFilteredLocations, getTabFolder, isFolder, translateBreadcrumb } from '@/utilities/homescreen.utilities';
import { sqItemsApi } from '@seeqdev/api/endpoint/ItemsApi';
import { SeeqNames } from '@seeqdev/constants';
import { fcMagGlassEmpty } from '@seeqdev/icons/seeq';
import { InputGroup } from '@seeqdev/qomponents';
import { colors } from '@seeqdev/qomponents/colors.stylex';
import type { TableColumn } from '@/core/Table.atom';
import type { Folder, Item } from '@/explorer/exploreModal.types';
import type { SearchItemLocation } from '@/main/app.constants';
import type { GetFoldersFilterEnum as FilterEnum } from '@seeqdev/api/endpoint/FoldersApi';

/** State slice the ExplorerModalBody reads from whichever store backs it. */
export interface ExplorerModalBodyStoreData {
  itemTotalForTable: number;
  pageSize: number;
  pageNumber: number;
  searchParams: {
    textSearch?: string;
    isPinned?: boolean;
    types?: string[];
    filter?: FilterEnum;
    folderId?: string;
  };
  tableFilter: string;
  items: { id: string }[];
  loading: boolean;
  folder:
    | {
        name: string;
        ancestors: any;
        id: string;
        translationKey?: string;
        owner?: any;
      }
    | undefined;
  initialFolderId?: string;
  showResults: boolean;
  sortProperty: string;
  sortAscending: boolean;
}

export interface ExplorerModalBodySupportingActions {
  setPageSizeForTable: ({ size, table }: { size: number; table?: string }) => void;
  setPageNumberAndGo: (page: number, loadTable: (...args: any[]) => any, table?: string) => void;
  setShowResults: (showResults: boolean) => void;
  setTableFilter: (tableFilter: SearchItemLocation) => void;
  setSearchParams: (field: string, value: any) => void;
  setFolder: (folder?: Folder) => void;
  setSort: (sortProperty: string, sortAscending: boolean, forceSortDirection?: boolean) => void;
  setWorkbookId: (workbookId?: string, workbookType?: string) => void;
  setTableRoot: (root?: string) => void;
  getRootAndUpdateFilter: (folderId: string) => void;
  setInitialFolderId: (initialFolderId?: string) => void;
  loadItems: () => void;
  clear: () => void;
}

interface ExplorerModalBodyProps {
  show: boolean;
  insideAdvancedSearch?: boolean;
  next?: () => void;
  itemLocationFilter?: any;
  breadcrumbsPresent?: boolean;
  advancedSearchSelectedFolder?: {
    id: string;
  };
  myFolderContent?: any;
  corporateFolderId?: string;
  /**
   * Pre-resolved store state that returns the explorer state slice. Defaults to reading from
   * the `ExploreWorkbookModal` Zustand store.
   */
  storeData?: ExplorerModalBodyStoreData;
  actions: ExplorerModalBodySupportingActions;
  allowedTypes?: string[];
  isSelectable?: boolean;
  selectOnlyWorkbooks?: boolean;
  selectableFolders?: boolean;
  setSelectedItem?: (item: any) => void;
  selectedItemIds?: string[];
  isMultiple?: boolean;
  searchPlaceholder?: string;
  initialFolderId?: string;
}

const DEFAULT_ALLOWED_TYPES = [SeeqNames.Types.Folder, SeeqNames.Types.Analysis];

export const ExplorerModalBody = ({
  next,
  show,
  storeData,
  actions,
  allowedTypes = DEFAULT_ALLOWED_TYPES,
  insideAdvancedSearch,
  itemLocationFilter,
  breadcrumbsPresent,
  advancedSearchSelectedFolder,
  myFolderContent,
  corporateFolderId,
  isSelectable,
  selectOnlyWorkbooks = false,
  selectableFolders = false,
  setSelectedItem,
  selectedItemIds: selectedItemIdsProp,
  isMultiple,
  searchPlaceholder,
}: ExplorerModalBodyProps) => {
  const { t } = useTranslation();

  const [searchString, setSearchString] = useState<React.ReactNode>();
  const [modalIsLoaded, setModalIsLoaded] = useState(false);
  const [firstRenderFolderId, setFirstRenderFolderId] = useState<string>();
  const [selectedItemIds, setSelectedItemIds] = useState<string[]>(selectedItemIdsProp ?? []);

  const workbookModalFolder = useExploreWorkbookModalFolder();
  const workbookModalInitialFolderId = useExploreWorkbookModalInitialFolderId();
  const workbookModalItems = useExploreWorkbookModalItems();
  const workbookModalLoading = useExploreWorkbookModalLoading();
  const workbookModalShowResults = useExploreWorkbookModalShowResults();
  const workbookModalSortAscending = useExploreWorkbookModalSortAscending();
  const workbookModalSortProperty = useExploreWorkbookModalSortProperty();
  const workbookModalTableFilter = useExploreWorkbookModalTableFilter();
  const workbookModalSearchParams = useExploreWorkbookModalSearchParams();
  const workbookModalItemTotalForTable = useExploreWorkbookModalItemTotalForTable();
  const workbookModalPageSize = useExploreWorkbookModalPageSize();
  const workbookModalPageNumber = useExploreWorkbookModalPageNumber();

  const defaultStoreData: ExplorerModalBodyStoreData = {
    folder: workbookModalFolder,
    initialFolderId: workbookModalInitialFolderId,
    items: workbookModalItems,
    loading: workbookModalLoading,
    showResults: workbookModalShowResults,
    sortAscending: workbookModalSortAscending,
    sortProperty: workbookModalSortProperty,
    tableFilter: workbookModalTableFilter,
    searchParams: workbookModalSearchParams,
    itemTotalForTable: workbookModalItemTotalForTable,
    pageSize: workbookModalPageSize,
    pageNumber: workbookModalPageNumber,
  };

  const data = storeData ?? defaultStoreData;
  const {
    folder,
    initialFolderId,
    items,
    loading: isLoading,
    showResults,
    sortAscending,
    sortProperty,
    tableFilter: filterValue,
    searchParams,
    itemTotalForTable: totalItems,
  } = data;
  const storedSearchText = searchParams.textSearch;
  const [searchText, setSearchText] = useState(storedSearchText);

  const workbookId = useFluxPath(sqWorkbookStore, (store) => store.workbookId);

  const showRecent = allowedTypes.length > 1 || selectOnlyWorkbooks;
  const locationOptions = getFilteredLocations(SEARCH_LOCATION_OPTIONS, showRecent);
  const parentFolder = insideAdvancedSearch ? myFolderContent?.username === folder?.name : undefined;
  const setSearchParamsToLoadItems = () => {
    if (itemLocationFilter?.value === SEARCH_ITEM_LOCATIONS.SHARED_OR_PUBLIC && folder) {
      actions.setSearchParams('filter', SEARCH_ITEM_LOCATIONS.SHARED_OR_PUBLIC);
      actions.setSearchParams('folderId', folder.id);
    } else if (itemLocationFilter?.value === SEARCH_ITEM_LOCATIONS.SHARED_OR_PUBLIC && !folder) {
      actions.setInitialFolderId();
      actions.setSearchParams('folderId', undefined);
    } else {
      actions.setSearchParams('folderId', initialFolderId);
    }
  };

  useEffect(() => {
    if (selectedItemIdsProp) {
      setSelectedItemIds(selectedItemIdsProp);
    }
  }, [selectedItemIdsProp]);

  useEffect(() => {
    if (insideAdvancedSearch) {
      setModalIsLoaded(true);
      setFirstRenderFolderId(initialFolderId);
      actions.setSearchParams('folderId', undefined);
      actions.setTableFilter(SEARCH_ITEM_LOCATIONS.ALL);
      actions.setTableRoot(undefined);
    }
    actions.setShowResults(false);
    actions.setSearchParams('textSearch', undefined);
  }, []);

  useEffect(() => {
    if (
      insideAdvancedSearch &&
      ((firstRenderFolderId !== initialFolderId && modalIsLoaded) ||
        (itemLocationFilter?.value === SEARCH_ITEM_LOCATIONS.SHARED_OR_PUBLIC && !firstRenderFolderId))
    ) {
      actions.setSearchParams('textSearch', undefined);
      actions.setSearchParams('filter', undefined);
      setSearchParamsToLoadItems();
      actions.setSearchParams('types', allowedTypes);
      actions.setShowResults(false);

      if (initialFolderId || itemLocationFilter?.value === SEARCH_ITEM_LOCATIONS.SHARED_OR_PUBLIC) {
        if (itemLocationFilter?.value === SEARCH_ITEM_LOCATIONS.USERS && folder?.ancestors?.length === 0) {
          setDropdownAndGo(SEARCH_ITEM_LOCATIONS.USERS);
        } else if (
          itemLocationFilter?.value === SEARCH_ITEM_LOCATIONS.SHARED_OR_PUBLIC &&
          (!folder || advancedSearchSelectedFolder?.id)
        ) {
          if (initialFolderId === advancedSearchSelectedFolder?.id || !advancedSearchSelectedFolder) {
            actions.setSearchParams('folderId', advancedSearchSelectedFolder?.id);
            actions.setSearchParams('filter', SEARCH_ITEM_LOCATIONS.SHARED_OR_PUBLIC);
            actions.loadItems();
          }
        } else {
          actions.loadItems();
        }
      }
    }
  }, [initialFolderId]);

  useEffect(() => {
    if (
      !insideAdvancedSearch ||
      (firstRenderFolderId === initialFolderId && initialFolderId) ||
      (breadcrumbsPresent && firstRenderFolderId !== initialFolderId && initialFolderId && firstRenderFolderId)
    ) {
      if (insideAdvancedSearch && (firstRenderFolderId === initialFolderId || breadcrumbsPresent)) {
        actions.setSearchParams('folderId', initialFolderId);
      }

      actions.setSearchParams('types', allowedTypes);
      actions.setTableRoot(filterValue === SEARCH_ITEM_LOCATIONS.USERS ? filterValue : undefined);

      if (initialFolderId) {
        if (
          itemLocationFilter?.value === SEARCH_ITEM_LOCATIONS.USERS &&
          (folder?.ancestors?.length === 0 || _.has(folder?.ancestors[0], 'translationKey'))
        ) {
          actions.setSearchParams('filter', SEARCH_ITEM_LOCATIONS.USERS);
        }
      }
      actions.loadItems();
    }
  }, [initialFolderId]);

  useEffect(() => {
    updateSearchString();
  }, [storedSearchText, filterValue, totalItems]);

  useEffect(() => {
    setSearchText(storedSearchText);
  }, [storedSearchText]);

  const executeSearch = (e: React.FormEvent) => {
    e.preventDefault();
    actions.setShowResults(true);
    actions.setTableFilter(SEARCH_ITEM_LOCATIONS.ALL);
    actions.setSearchParams('textSearch', searchText);
    actions.setSearchParams('onlyPinned', false);
    actions.setSearchParams('types', allowedTypes);
    actions.setSearchParams('filter', undefined);
    actions.setSearchParams('folderId', undefined);
    actions.setFolder(undefined);
    actions.setSort('name', true, true);
    actions.setPageNumberAndGo(1, actions.loadItems);
  };

  const updateSearchString = () => {
    const bold = (text?: string) => <span {...stylex.props(styles.searchTerm)}>{text}</span>;
    const resultString = filterValue && (
      <span>
        {totalItems ? t('HOME_SCREEN.SHOWING_RESULTS_FOR') : t('HOME_SCREEN.NO_RESULTS_FOR')} {bold(storedSearchText)}
        {` ${t('HOME_SCREEN.IN')}`} {bold(t((_.find(locationOptions, { value: filterValue }) as any)?.text))}.
      </span>
    );
    setSearchString(resultString);
  };

  const selectableColumn: TableColumn[] = isSelectable
    ? [
        {
          accessor: 'id',
          sortable: false,
          filterable: false,
          cellRenderFunction: (item) =>
            isFolder(item) && !selectableFolders ? (
              <></>
            ) : isFolder(item) && selectableFolders ? (
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  updateSelectedItemIds({ ...item, type: item.type ?? '' });
                }}>
                <Checkbox
                  type={isMultiple ? 'checkbox' : 'radio'}
                  id="cloneToAnotherWorkbook"
                  isChecked={selectedItemIds.includes(item.id)}
                  label=""
                />
              </div>
            ) : (
              <Checkbox
                type={isMultiple ? 'checkbox' : 'radio'}
                id="cloneToAnotherWorkbook"
                isChecked={selectedItemIds.includes(item.id)}
                label=""
              />
            ),
          cellStyle: { width: 40, maxWidth: 40 },
        },
      ]
    : [];

  const otherColumns: TableColumn[] = [
    {
      accessor: 'id',
      header: '',
      cellRenderFunction: (item) => <HomeScreenItemIconCell item={item} />,
      cellStyle: { width: 40 },
      filterable: false,
      sortable: false,
    },
    {
      accessor: 'name',
      header: 'EXPLORER_MODAL.NAME',
      cellRenderFunction: (item) => (
        <NameCell
          item={item}
          showBreadcrumbs={_.isUndefined(folder)}
          xstyle={styles.itemName}
          isCurrentWorkbook={workbookId === item.id}
        />
      ),
      filterable: false,
      sortable: filterValue !== SEARCH_ITEM_LOCATIONS.RECENT,
    },
    {
      accessor: 'owner.name',
      headerRenderFunction: () => renderOwnerHeader(),
      filterable: false,
      sortable: filterValue !== SEARCH_ITEM_LOCATIONS.RECENT,
    },
    {
      accessor: 'updatedAt',
      cellType: CELL_TYPES.DATE_TIME,
      header: 'EXPLORER_MODAL.LAST_UPDATED',
      filterable: false,
      sortable: filterValue !== SEARCH_ITEM_LOCATIONS.RECENT,
    },
  ];

  const columns: TableColumn[] = selectableColumn.concat(otherColumns);

  const openFolder = (folder: Folder) => {
    if (itemLocationFilter?.value === SEARCH_ITEM_LOCATIONS.SHARED_OR_PUBLIC && insideAdvancedSearch) {
      actions.setInitialFolderId();
    }
    actions.setSort('name', true, true);
    actions.setShowResults(false);
    actions.setFolder(folder);
    actions.setSearchParams(
      'filter',
      filterValue === SEARCH_ITEM_LOCATIONS.SHARED_OR_PUBLIC ? SEARCH_ITEM_LOCATIONS.SHARED_OR_PUBLIC : undefined,
    );
    actions.setSearchParams(
      'filter',
      filterValue === SEARCH_ITEM_LOCATIONS.USERS ? SEARCH_ITEM_LOCATIONS.USERS : undefined,
    );
    actions.setWorkbookId(undefined);
    actions.setSearchParams('folderId', folder?.id);
    actions.setSearchParams('onlyPinned', false);
    actions.setSearchParams('textSearch', undefined);
    actions.setSearchParams('types', allowedTypes);
    actions.setPageNumberAndGo(1, actions.loadItems);
  };

  const openAnalysis = (workbookId: string, workbookType: string) => {
    actions.setWorkbookId(workbookId, workbookType);
    next?.();
  };

  const updateSelectedItemIds = (item: Item) => {
    setSelectedItemIds((selectedItemIds) => {
      if (isMultiple) {
        return selectedItemIds.includes(item.id)
          ? selectedItemIds.filter((id) => id !== item.id)
          : selectedItemIds.concat(item.id);
      }
      return [item.id];
    });
    setSelectedItem?.(item);
  };

  const handleItemSelection = (item: Item) => {
    if (isFolder(item)) {
      openFolder(item);
    } else {
      if (isSelectable) {
        updateSelectedItemIds(item);
      }
      openAnalysis(item.id, item.type);
    }
  };

  const folderTree = (location: SearchItemLocation) => {
    switch (location) {
      case SEARCH_ITEM_LOCATIONS.MY_FOLDER:
        return getTabFolder(HOME_SCREEN_TABS.MY_FOLDER);
      case SEARCH_ITEM_LOCATIONS.CORPORATE:
        return getTabFolder(HOME_SCREEN_TABS.CORPORATE);
      case SEARCH_ITEM_LOCATIONS.USERS:
        return getTabFolder(HOME_SCREEN_TABS.USERS);
      case SEARCH_ITEM_LOCATIONS.EXTERNALLY_SHARED:
        return getTabFolder(HOME_SCREEN_TABS.EXTERNALLY_SHARED);
      default:
        return Promise.resolve(undefined);
    }
  };

  const setDropdownAndGo = (value: SearchItemLocation) => {
    const isPinned = value === SEARCH_ITEM_LOCATIONS.PINNED;
    const isRecent = value === SEARCH_ITEM_LOCATIONS.RECENT;
    const folderPromise = folderTree(value);
    const folderLocations = [
      SEARCH_ITEM_LOCATIONS.MY_FOLDER,
      SEARCH_ITEM_LOCATIONS.CORPORATE,
      SEARCH_ITEM_LOCATIONS.USERS,
      SEARCH_ITEM_LOCATIONS.EXTERNALLY_SHARED,
    ];

    folderPromise.then((folder: Folder) => {
      actions.setTableRoot(value === SEARCH_ITEM_LOCATIONS.USERS ? value : undefined);
      // We store this separately because we don't always use the value of the filter in the query
      actions.setTableFilter(value);
      actions.setFolder(folder);
      if (_.isNil(folder) && _.includes(folderLocations, value)) {
        // In the case where the My Folder has not yet been created
        actions.setSearchParams('folderId', value);
        actions.setSearchParams('filter', undefined);
      } else {
        actions.setSearchParams('folderId', folder?.id);
        actions.setSearchParams('filter', isPinned || isRecent ? undefined : value);
      }
      actions.setSearchParams('onlyPinned', isPinned);
      actions.setSearchParams('types', isRecent ? _.without(allowedTypes, 'Folder') : allowedTypes);
      actions.setSort(isRecent ? 'openedAt' : 'name', !isRecent, true);
      actions.setPageNumberAndGo(1, actions.loadItems);
    });
  };

  const clearSearch = () => {
    actions.setShowResults(false);
    actions.setSearchParams('textSearch', undefined);
    actions.setSearchParams('folderId', undefined);
    actions.setFolder(undefined);
    actions.setPageNumberAndGo(1, actions.loadItems);
  };

  const formatBreadcrumb = (crumb: Folder) => {
    const itemContainsOwner = _.has(crumb, 'owner');
    return (
      <div
        key={`explorerCrumbId_${crumb?.id}`}
        {...stylex.props(styles.breadcrumb)}
        // Existing MoveItem browser page object uses this interaction hook.
        className={clsx('breadcrumbLink', stylex.props(styles.breadcrumb).className)}
        onClick={() => clickBreadcrumb(crumb)}>
        {translateBreadcrumb(
          crumb,
          itemContainsOwner &&
            insideAdvancedSearch &&
            myFolderContent?.id &&
            itemLocationFilter?.value === SEARCH_ITEM_LOCATIONS.MY_FOLDER
            ? crumb.owner.id === myFolderContent?.id
            : false,
          corporateFolderId,
        )}
        <span {...stylex.props(styles.breadcrumbSeparator)}>»</span>
      </div>
    );
  };

  const clickBreadcrumb = (crumb: Folder) => {
    // Scrape the breadcrumbs from the current folder rather than having to make another call to the backend
    const newFolder = _.assign({}, crumb, {
      ancestors: _.slice(folder!.ancestors, 0, _.indexOf(folder!.ancestors, crumb)),
    });

    // If the crumb doesn't have an id, it is not a real folder, ie. 'Shared'
    const folderLocation =
      newFolder.name === 'Shared' ? SEARCH_ITEM_LOCATIONS.SHARED_OR_PUBLIC : _.lowerCase(newFolder.name);

    if (newFolder.id) {
      // Since the breadcrumbs don't include effective permissions, we have to get the folder details
      return sqItemsApi
        .getItemAndAllProperties({ id: newFolder.id })
        .then(({ data: response }) => openFolder(_.assign(response, newFolder)));
    }

    return setDropdownAndGo(folderLocation as SearchItemLocation);
  };

  const sort = (sortProperty: string, sortAscending: boolean) => {
    actions.setSort(sortProperty, sortAscending);
    actions.setPageNumberAndGo(1, actions.loadItems);
  };

  const renderOwnerHeader = () => {
    const supportSorting = filterValue !== SEARCH_ITEM_LOCATIONS.RECENT;
    return (
      <div
        onClick={() => (supportSorting ? sort('owner', sortAscending) : _.noop())}
        {...stylex.props(
          styles.ownerHeader,
          supportSorting && styles.sortableHeader,
          sortProperty === 'owner' && styles.sortedHeader,
        )}
        className={clsx(
          'flexColumnContainer',
          stylex.props(
            styles.ownerHeader,
            supportSorting && styles.sortableHeader,
            sortProperty === 'owner' && styles.sortedHeader,
          ).className,
        )}>
        {t('EXPLORER_MODAL.OWNER')}
        {supportSorting && (
          <div data-testid="owner_sort">
            <SortIcon sortProperty={sortProperty} sortBy="owner" sortAsc={sortAscending} />
          </div>
        )}
      </div>
    );
  };

  // This component is used in a modal with state, return null if we aren't in the proper state to show this component
  if (!show) return null;

  const getPlaceholderText = () => {
    if (searchPlaceholder) {
      return searchPlaceholder;
    } else if (_.isEqual(allowedTypes, [SeeqNames.Types.Analysis])) {
      return t('EXPLORER_MODAL.SEARCH_ANALYSIS_PLACEHOLDER');
    } else if (_.isEqual(allowedTypes, [SeeqNames.Types.Folder])) {
      return t('EXPLORER_MODAL.SEARCH_FOLDER_PLACEHOLDER');
    } else if (allowedTypes.includes(SeeqNames.Types.Vantage)) {
      return t('EXPLORER_MODAL.SEARCH_VANTAGE_PLACEHOLDER');
    }
    return t('EXPLORER_MODAL.SEARCH_PLACEHOLDER');
  };

  return (
    <div {...stylex.props(styles.panel)} className={clsx('flexRowContainer', stylex.props(styles.panel).className)}>
      <div
        {...stylex.props(styles.content)}
        className={clsx('flexRowContainer', stylex.props(styles.content).className)}>
        <form
          onSubmit={executeSearch}
          {...stylex.props(styles.searchForm)}
          className={clsx('flexColumnContainer', stylex.props(styles.searchForm).className)}
          data-testid="explorerModalSearchBar">
          <InputGroup
            value={searchText ? searchText : ''}
            containerXstyle={styles.searchInputContainer}
            xstyle={styles.searchInput}
            placeholder={getPlaceholderText()}
            testId="explorerModalSearchInput"
            onFocus={(e: React.FocusEvent<HTMLInputElement>) => e.target.select()}
            onChange={(e) => setSearchText(e.target.value)}
            append={[
              {
                variant: 'button',
                buttonProps: {
                  icon: fcMagGlassEmpty,
                  iconStyle: 'theme',
                  testId: 'explorerModalSearchButton',
                  xstyle: styles.searchButton,
                  onClick: executeSearch,
                },
              },
            ]}
          />
        </form>
        {!insideAdvancedSearch && (
          <span {...stylex.props(styles.locationSelect)} data-testid="explorerModalLocationSelect">
            <IconSelect
              extraClassNames="specExplorerModalLocationSelect"
              selectOptions={locationOptions as any}
              name="location"
              value={filterValue}
              onChange={(option) => setDropdownAndGo(option.value as SearchItemLocation)}
            />
          </span>
        )}

        {showResults && <SearchResults searchString={searchString} isLoading={isLoading} onClear={clearSearch} />}

        {folder?.ancestors && (
          <div
            id="breadcrumbLinks"
            {...stylex.props(styles.breadcrumbs)}
            className={clsx('flexColumnContainer', stylex.props(styles.breadcrumbs).className)}>
            {_.map(folder.ancestors, formatBreadcrumb)}
            {translateBreadcrumb(
              folder,
              parentFolder && itemLocationFilter?.value === SEARCH_ITEM_LOCATIONS.MY_FOLDER,
              corporateFolderId,
            )}
          </div>
        )}

        <span {...stylex.props(styles.tableViewport)}>
          <ServerSideFilteringAndPaginatingTable
            tableXstyle={styles.resultsTable}
            headerCellXstyle={styles.tableHeader}
            cellXstyle={styles.tableCell}
            rowXstyle={(index) => [styles.tableRow, index % 2 === 0 && styles.alternateRow]}
            resizerXstyle={styles.resizer}
            columns={columns}
            items={items}
            selectedIds={selectedItemIds}
            rowClickCallback={handleItemSelection}
            rowSelectionCallback={_.noop}
            filterCallback={_.noop}
            sortCallback={sort}
            selectAllCallback={_.noop}
            data={data}
            actions={actions}
            insideModal={true}
            loadTable={actions.loadItems}
            testId="exploreWorkbookModalTable"
          />
        </span>
      </div>
    </div>
  );
};

// Flex hooks preserve the shared print/PDF layout contract.
const styles = stylex.create({
  panel: { display: 'flex', flexDirection: 'column' },
  content: { display: 'flex', flexDirection: 'column', minHeight: 325, maxHeight: 405 },
  searchTerm: { fontWeight: 'bolder' },
  breadcrumb: { cursor: 'pointer', color: colors.legacyToolsPrimary },
  breadcrumbSeparator: { marginLeft: 3, marginRight: 3 },
  ownerHeader: { display: 'flex', flexDirection: 'row', marginBottom: 5, fontWeight: 600 },
  sortableHeader: { cursor: 'pointer' },
  sortedHeader: { color: colors.legacyToolsLink },
  searchForm: { display: 'flex', flexDirection: 'row', flexWrap: 'nowrap', marginBottom: 10, padding: 0, height: 34 },
  itemName: { cursor: 'pointer' },
  searchInputContainer: {
    borderTopRightRadius: { 'default': null, ':last-child': 6 },
    borderBottomRightRadius: { 'default': null, ':last-child': 6 },
  },
  searchInput: {
    borderTopRightRadius: { 'default': null, ':last-child': 6 },
    borderBottomRightRadius: { 'default': null, ':last-child': 6 },
  },
  searchButton: {
    borderTopRightRadius: 6,
    borderBottomRightRadius: 6,
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
  },
  locationSelect: { marginBottom: 10, width: 250 },
  breadcrumbs: { display: 'flex', flexDirection: 'row', flexWrap: 'wrap' },
  resultsTable: {
    width: '99%',
    marginBottom: '1rem',
    paddingBottom: 65,
    color: colors.bodyText,
    borderCollapse: 'collapse',
  },
  tableHeader: {
    padding: '0.496rem',
    verticalAlign: 'top',
    borderWidth: 0,
    borderBottomWidth: 1,
    borderBottomStyle: 'solid',
    borderBottomColor: colors.toolTableHeaderBorder,
    backgroundColor: colors.panelBackground,
  },
  tableCell: { padding: '0.496rem', verticalAlign: 'top', borderWidth: 0 },
  tableRow: { backgroundColor: { 'default': null, ':hover': colors.toolTableRowHover } },
  alternateRow: { backgroundColor: { 'default': colors.tableRowAlternate, ':hover': colors.toolTableRowHover } },
  resizer: { cursor: 'col-resize' },
  tableViewport: { maxHeight: 325, overflow: 'auto' },
});
